<template>
	<div>
		<Loader v-if="loading" />
		<div v-if="loaded && !loading">
			<div class="margin-bottom">
				<button v-on:click="autoSelect()">Auto-select</button>
			</div>

			<Loader v-if="soft_loading" :soft="true" />

 			<div class="border small-padding flex full-width">
 				<div class="flex-basis">
					<div class="flex">
						<MaterialText v-model="street_name" label="Street name" />
						<MaterialText v-model="shipping.street_number" label="Street number" />
					</div>
					<div class="margin-top flex">
						<MaterialText v-model="shipping.door" label="House/door number" />
					</div>
					<ValidationObserver ref="form" class="margin-top flex">
            <ValidationProvider
              v-slot="{ errors }"
              :rules="{ required: true, zipcode: { regex: zipcodeStyle } }">
              <div class="relative">
                <MaterialText v-on:input="zipcodeInput()" :disabled="inputDisabled" v-model="shipping.zipcode" label="Zipcode" class="small-margin-right" />
                <span
                  v-show="shipping.zipcode"
                  class="zipcode-clear"
                  @click="clearZipcode"
                >
                  ✖
                </span>
              </div>
              <label class="text-error">{{ errors[0] }}</label>
            </ValidationProvider>
						<MaterialText v-model="shipping.city" label="City" />
					</ValidationObserver>
					<div class="margin-top">
						<MaterialDropdown label="Country" :options="countries" v-model="shipping.country_id" />
					</div>
					<div class="margin-top">
						<MaterialToggle v-model="update_unshipped_orders" label="Apply on (all) unshipped orders" />
					</div>
					<div>
						<button v-on:click="saveAddress()" class="small-margin-top" :disabled="saving">Save</button>
					</div>
 				</div>
 				<div class="flex-basis">
 					<div class="input-row small-margin-bottom">
						<span class="label">Parcelshop</span>
						<div>{{ shipping.parcelshop_id }}</div>
					</div>
					<div class="input-row small-margin-bottom">
						<span class="label">Shipping method</span>
						<div>{{ shipping.shipping_method.name }}</div>
					</div>
 				</div>
 			</div>

			<h2 class="large-margin-top margin-bottom">Change parcelshop ({{ parcelshops.length }})</h2>
			<div class="flex">

				<div class="flex small-margin-right">
					<MaterialText v-model="parcelshop_street_name" label="Street Name" />
					<MaterialText v-model="parcelshop_street_number" label="Street number" />
				</div>
			</div>
			<div class="flex margin-top">
				<div class="flex small-margin-right">
					<MaterialText v-on:enter="search()" :disabled="saving" v-model="parcelshop_zipcode" label="Zipcode" class="small-margin-left short" />
				</div>

				<button v-on:click="search()">Search</button>
				<div class="margin-left">
					<MaterialToggle v-model="update_unshipped_orders" label="Apply on (all) unshipped orders" />
				</div>
			</div>
			<div class="margin-top" v-if="parcelshops.length">
				<table class="basic">
					<thead>
						<tr>
							<th>ID</th>
							<th>Parcelshop</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="parcelshop in parcelshops">
							<td>{{ parcelshop.id }}</td>
							<td>{{ parcelshop.name }}</td>
							<td>
								<button :disabled="saving" v-on:click="setParcelshop(parcelshop.id)" class="smaller white">Select</button>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
		<div v-if="!loading && !loaded && !autoload">
			<button v-on:click="load()">Load</button>
		</div>
	</div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import { ValidationProvider, ValidationObserver } from 'vee-validate'

require('./ValidationRules.js')

export default {
  components: {
    ValidationProvider,
    ValidationObserver
  },
  props: [
    'id',
    'autoload'
  ],
  data: () => ({
    saving: false,
    loading: false,
    soft_loading: false,
    shipping: {},
    parcelshops: [],
    update_unshipped_orders: false,
    locale_id: false,
    parcelshop_zipcode: '',
    parcelshop_street_number: '',
    parcelshop_street_name: '',
    inputDisabled: false,
    zipcode_regex: {
      1: '/\\d{4}/', // da_DK:
      2: '/\\d{3}\\s?\\d{2}/', // sv_SE:
      3: '/\\d{4}\\s?[a-zA-Z]{2}/', // nl_NL:
      4: '/^\\d{5}$/' // de_DE:
    }
  }),
  computed: {
    ...mapGetters([
      'getCountries'
    ]),
    zipcodeStyle () {
      try {
        if (this.shipping) {
          const obj = this.zipcodeRegex[this.shipping.country_id]
          return new RegExp(obj.replace(/\//g, ''))
        } else {
          const obj = this.zipcodeRegex[1]
          return new RegExp(obj.replace(/\//g, ''))
        }
      } catch (error) {
        const obj = this.zipcode_regex[this.shipping.country_id]
        return new RegExp(obj.replace(/\//g, ''))
      }
    },
    loaded () {
      return Object.keys(this.shipping).length > 0
    },
    countries () {
      return Vue.$array.keyValue(this.getCountries, 'id', 'name')
    },
    zipcodeRegex () {
      return Vue.$array.keyValue(this.getCountries, 'id', 'zipcode_regex')
    },
    shippingAddress () {
      return this.shipping.street_name + ' ' + this.shipping.street_number + ', ' + this.shipping.door
    },
    parcelshop_address () {
      return this.parcelshop_street_name + ' ' + this.parcelshop_street_number
    },
    /**
     * @see https://app.clickup.com/t/345z660
     * Relevant to fix street number and door
     */
    street_name: {
      get: function () {
        return this.shipping.street_name
      },
      set: function (val) {
        const splitStreet = val.split(',')
        const door = splitStreet.length > 1 ? splitStreet[splitStreet.length - 1].trim() : ''

        const splitHouse = splitStreet.length > 0 ? splitStreet[0].split(' ') : splitStreet.split(' ')
        const street_number = splitHouse.length > 0 ? this.isNumber(splitHouse[splitHouse.length - 1].charAt()) ? splitHouse[splitHouse.length - 1] : '' : ''

        const streetName = val.replace(/([a-zA-Z0-9\.\- ]+) \d+[a-zA-Z]*(,? (.*))?/, '$1')

        const removeIndex = val.indexOf(street_number || (door || ''))
        this.shipping.street_name = (street_number || door) ? val.substring(0, removeIndex).trim() : streetName
        this.shipping.street_number = this.shipping.street_number ? this.shipping.street_number : street_number
        this.shipping.door = this.shipping.door ? this.shipping.door : door
      }
    }
  },
  methods: {
    isNumber (n) { return !isNaN(parseFloat(n)) && isFinite(n) },
    autoSelect () {
      Vue.$api.post('/customer/' + this.id + '/shipping/auto-select', {}, {
        loader: toggle => this.soft_loading = toggle,
        callback: data => this.load()
      })
    },
    zipcodeInput () {
      const zipcode = this.shipping.zipcode
      if (!zipcode || zipcode.length === 0) return
      if (zipcode.match(this.zipcodeStyle)) {
        this.inputDisabled = true
      } else {
        this.inputDisabled = false
      }
    },
    clearZipcode () {
      this.shipping.zipcode = ''
      this.inputDisabled = false
    },
    search () {
      var url = '/shipping/parcelshops?street=' + this.parcelshop_address +
					'&zipcode=' + this.parcelshop_zipcode +
					'&locale_id=' + this.shipping.shipping_method.locale.locale_id
      console.log(url)
      Vue.$api.get(url, {}, {
        loader: toggle => this.soft_loading = toggle,
        callback: data => this.parcelshops = data
      })
    },
    setParcelshop (id) {
      Vue.$api.patch('/customer/' + this.getId() + '/shipping/parcelshop', {
        parcelshop_id: id,
        update_unshipped_orders: this.update_unshipped_orders
      }, {
        loader: toggle => this.saving = toggle,
        callback: () => this.load()
      })
    },
    saveAddress () {
      this.$refs.form.validate().then((success) => {
        if (success) {
          Vue.$api.patch('/customer/' + this.getId() + '/shipping/address', {
            street_name: this.shipping.street_name,
            street_number: this.shipping.street_number,
            door: this.shipping.door,
            address: this.shippingAddress,
            zipcode: this.shipping.zipcode,
            city: this.shipping.city,
            country_id: this.shipping.country_id,
            update_unshipped_orders: this.update_unshipped_orders
          }, {
            loader: toggle => this.saving = toggle,
            callback: () => this.load()
          })
        }
      })
    },
    load () {
      Vue.$api.get('/customer/' + this.getId() + '/shipping', {}, {
        loader: toggle => this.loading = toggle,
        callback: data => this.parse(data)
      })
    },
    parse (data) {
      this.shipping = data
      this.parcelshop_street_number = data.street_number
      this.parcelshop_street_name = data.street_name
      this.parcelshop_zipcode = data.zipcode
    },
    getId () {
      return this.id
    }
  },
  created () {
    if (this.autoload) {
      this.load()
    }
  }
}
</script>

<style lang="less" scoped>

.zipcode-clear {
  display: inline-block;
  width: 20px;
  height: 20px;
  text-align: center;
  position: absolute;
  right: 10px;
  top: 50%;
  z-index: 23;
  transform: translate(-50%, -50%);
  cursor: pointer;
}
</style>
